import { all, call, takeLeading, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

import {
  requestRetrieveFrequentlyAskedQuestions,
  requestUpdateFrequentlyAskedQuestions,
  requestCreateTutorial,
  requestRetrieveTutorials,
  requestUpdateTutorial,
  requestDeleteTutorial,
} from './help.actions';
import {
  getRetrieveFrequentlyAskedQuestionsRequest,
  getUpdateFrequentlyAskedQuestionsRequest,
  getCreateTutorialRequest,
  getRetrieveTutorialsRequest,
  getUpdateTutorialRequest,
  getDeleteTutorialRequest,
} from './help.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setFrequentlyAskedQuestions, setTutorials } from '../../slices/help-center/help-center.slice';

export function* performRetrieveFrequentlyAskedQuestionsRequest() {
  try {
    yield put(addSpinner('Retrieving Frequently Asked Questions'));

    const [endpoint, options] = getRetrieveFrequentlyAskedQuestionsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setFrequentlyAskedQuestions(data.updatedFAQ));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Frequently Asked Questions'));
  }
}

export function* watchForRetrieveFrequentlyAskedQuestionsRequest() {
  yield takeLeading(requestRetrieveFrequentlyAskedQuestions.type, performRetrieveFrequentlyAskedQuestionsRequest);
}

export function* performUpdateFrequentlyAskedQuestionsRequest({ payload }) {
  const { input } = payload;

  try {
    yield put(addSpinner('Updating Frequently Asked Questions'));

    const [endpoint, options] = getUpdateFrequentlyAskedQuestionsRequest(input);

    const { data } = yield call(axios, endpoint, options);

    yield put(setFrequentlyAskedQuestions(data.updatedFAQ));

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Frequently Asked Questions'));
  }
}

export function* watchForUpdateFrequentlyAskedQuestionsRequest() {
  yield takeLeading(requestUpdateFrequentlyAskedQuestions.type, performUpdateFrequentlyAskedQuestionsRequest);
}

export function* performCreateTutorialRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Tutorial'));

    const [endpoint, options] = getCreateTutorialRequest(input);

    const { data } = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(requestRetrieveTutorials());

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Tutorial'));
  }
}

export function* watchForCreateTutorialRequest() {
  yield takeLeading(requestCreateTutorial.type, performCreateTutorialRequest);
}

export function* performRetrieveTutorialsRequest() {
  try {
    yield put(addSpinner('Retrieving Tutorials'));

    const [endpoint, options] = getRetrieveTutorialsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setTutorials(data.tutorialResponseVOList));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Tutorials'));
  }
}

export function* watchForRetrieveTutorialsRequest() {
  yield takeLeading(requestRetrieveTutorials.type, performRetrieveTutorialsRequest);
}

export function* performUpdateTutorialRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Tutorial'));

    const [endpoint, options] = getUpdateTutorialRequest(input);

    const { data } = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(requestRetrieveTutorials());

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Tutorial'));
  }
}

export function* watchForUpdateTutorialRequest() {
  yield takeLeading(requestUpdateTutorial.type, performUpdateTutorialRequest);
}

export function* performDeleteTutorialRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner('Deleting Tutorial'));

    const [endpoint, options] = getDeleteTutorialRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(requestRetrieveTutorials());
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Deleting Tutorial'));
  }
}

export function* watchForDeleteTutorialRequest() {
  yield takeLeading(requestDeleteTutorial.type, performDeleteTutorialRequest);
}

export default function* helpSaga() {
  yield all([
    watchForRetrieveFrequentlyAskedQuestionsRequest(),
    watchForUpdateFrequentlyAskedQuestionsRequest(),
    watchForCreateTutorialRequest(),
    watchForRetrieveTutorialsRequest(),
    watchForUpdateTutorialRequest(),
    watchForDeleteTutorialRequest(),
  ]);
}
